import React from "react";
import { Badge } from "./badge";
import { Book } from "lucide-react";

export interface ClassCardItem {
  title: string;
  status: string;
  imageUrl: string;
  totalLessons: number;
  totalUnits: number;
  lessonsCompleted: number;
  unitsCompleted: number;
  completionPercent: number;
  purchasedOn: string;
  expiryOn: string;
  onViewClass?: () => void;
  viewClassDisabled?: boolean;
}

interface ClassCardProps {
  classItem: ClassCardItem;
}

interface CheckboxItemProps {
  label: string;
  value: string;
}

const CheckboxItem: React.FC<CheckboxItemProps> = ({ label, value }) => (
  <div className="flex items-center">
    <Book className="h-[18px] w-[18px] text-[#929292] mr-2" />
    <span className="text-[#1c1a4a] text-[16px] font-medium">
      {label} {value}
    </span>
  </div>
);

interface CircularProgressProps {
  percent: number;
  color: string;
  label: string;
  value: string;
}

const CircularProgress: React.FC<CircularProgressProps> = ({
  percent,
  color,
  label,
  value,
}) => {
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const calculatedPercent = percent === 100 ? 99.9999 : percent;
  const strokeDasharray = `${
    (calculatedPercent / 100) * circumference
  } ${circumference}`;

  return (
    <div className="flex flex-col items-center">
      <div className="relative h-[68.5px] w-[68.5px]">
        <svg className="h-full w-full" viewBox="0 0 36 36">
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            className="stroke-gray-200"
            strokeWidth="3"
          />
          <circle
            cx="18"
            cy="18"
            r="16"
            fill="none"
            className={color}
            strokeWidth="3"
            strokeDasharray={strokeDasharray}
            strokeDashoffset="0"
            strokeLinecap="round"
            transform="rotate(-90 18 18)"
          />
        </svg>
        <span className="text-[16px] font-[400] block text-center text-[#0A1929D9] mt-[10px]">
          {label}
        </span>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[16px] font-bold text-[#0A1929]">{value}</span>
        </div>
      </div>
    </div>
  );
};

const getBadgeVariant = (status: string) => {
  const normalized = status.toUpperCase().replace(/\s+/g, "_");
  switch (normalized) {
    case "NEW":
    case "IN_PROGRESS":
      return "primary" as const;
    case "COMPLETED":
      return "success" as const;
    case "REVIEW":
      return "warning" as const;
    case "PENDING":
      return "danger" as const;
    default:
      return "primary" as const;
  }
};

export function formatClassStatusLabel(status: string): string {
  return status
    .trim()
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function formatClassDashboardDate(value: string | null): string {
  if (!value) return "N/A";
  const parsed = new Date(value.replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return value;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

const ClassCard: React.FC<ClassCardProps> = ({ classItem }) => {
  const lessonsPercent =
    classItem.totalLessons > 0
      ? Math.round((classItem.lessonsCompleted / classItem.totalLessons) * 100)
      : 0;
  const unitsPercent =
    classItem.totalUnits > 0
      ? Math.round((classItem.unitsCompleted / classItem.totalUnits) * 100)
      : 0;
  const statusLabel = formatClassStatusLabel(classItem.status);

  return (
    <div
      className="flex xl:flex-row flex-col justify-between border border-[#E7EAEC] rounded-[12px] p-[16px]"
      style={{
        background:
          "linear-gradient(103.76deg, rgba(255, 167, 69, 0.1) 9.84%, rgba(254, 134, 159, 0.1) 29.92%, rgba(239, 122, 200, 0.1) 50%, rgba(160, 131, 237, 0.1) 70.08%, rgba(67, 174, 255, 0.1) 90.16%)",
      }}
    >
      <div className="flex md:flex-row flex-col">
        <div className="w-[284px] h-[152px] rounded-[8px] shrink-0">
          <img
            src={classItem.imageUrl}
            alt={`${classItem.title} thumbnail`}
            className="w-full h-full rounded-[8px] object-cover"
          />
        </div>

        <div className="md:px-[16px] px-0 md:pt-[10px] pt-[16px]">
          <h3
            className="text-[#161439] text-[18px] font-semibold"
            style={{ lineHeight: "25px" }}
          >
            {classItem.title}
          </h3>
          <Badge
            variant={getBadgeVariant(classItem.status)}
            className="py-[8px] px-[10px] h-[32px] min-w-[130px] mt-[8px] mb-[10px] rounded-[70px] font-medium text-[#1F1F1F]"
          >
            {statusLabel}
          </Badge>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <CheckboxItem label="Lessons" value={String(classItem.totalLessons)} />
            <CheckboxItem label="Units" value={String(classItem.totalUnits)} />
          </div>
          <div className="mt-[10px] space-y-1 text-[16px] font-medium text-[#1c1a4a]">
            <p>Purchased on: {classItem.purchasedOn}</p>
            <p>Expiry on: {classItem.expiryOn}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center md:flex-row flex-col xl:my-0 md:my-[20px] mt-[30px]">
        <div className="flex justify-between gap-x-[20px] items-center md:mr-[80px] mb-4">
          <CircularProgress
            percent={lessonsPercent}
            color="stroke-[#FD7E14]"
            label="Lessons"
            value={`${classItem.lessonsCompleted}/${classItem.totalLessons}`}
          />
          <div className="w-[1px] h-[32px] bg-[#c4c4c4]" />
          <CircularProgress
            percent={unitsPercent}
            color="stroke-[#D6588B]"
            label="Units"
            value={`${classItem.unitsCompleted}/${classItem.totalUnits}`}
          />
          <div className="w-[1px] h-[32px] bg-[#c4c4c4]" />
          <CircularProgress
            percent={classItem.completionPercent}
            color="stroke-[#5C88C5]"
            label="Completion"
            value={`${classItem.completionPercent}%`}
          />
        </div>
        <button
          type="button"
          onClick={classItem.onViewClass}
          disabled={classItem.viewClassDisabled}
          className="bg-[#5C88C5] md:mt-0 mt-[30px] w-[161px] h-[40px] text-white px-[12px] py-[8px] text-[16px] font-[600] rounded-[12px] disabled:cursor-default disabled:opacity-60"
        >
          View Class
        </button>
      </div>
    </div>
  );
};

export default ClassCard;
